import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Grid,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import FeeTermsFields from '../FeeTermsFields';
import { useCurrency } from '../../context/CurrencyContext';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const UnitManagement = ({ propertyId, onClose }) => {
  const { formatMoney } = useCurrency();
  const [units, setUnits] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleUnits, resultCount, totalCount
  } = useTableSearch(units, {
    searchFields: ['unitNumber', 'unitName', 'assignedTenant.name'],
    filters: {
      status: (row, v) => row.status === v
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    unitNumber: '',
    bedrooms: 1,
    bathrooms: 1,
    squareFeet: 0,
    monthlyRent: 0,
    deposit: 0,
    status: 'available',
    amenities: [],
    description: '',
    feeType: '',
    feeValue: '',
  });

  useEffect(() => {
    if (propertyId) {
      fetchUnits();
    }
    // Keyed on the property being shown. fetchUnits is declared below and reads only
    // propertyId, so naming it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchUnits = async () => {
    try {
      const response = await axios.get(`/units/property/${propertyId}`);
      setUnits(response.data);
    } catch (error) {
      toast.error('Error fetching units');
    }
  };

  const handleOpen = (unit = null) => {
    if (unit) {
      setEditing(unit._id);
      setFormData({
        unitNumber: unit.unitNumber || '',
        bedrooms: unit.bedrooms || 1,
        bathrooms: unit.bathrooms || 1,
        squareFeet: unit.squareFeet || 0,
        monthlyRent: unit.monthlyRent || 0,
        deposit: unit.deposit || 0,
        status: unit.status || 'available',
        amenities: unit.amenities || [],
        description: unit.description || '',
        // '' rather than a default: blank means "inherit the property's terms", which is a
        // different arrangement from an explicit 0.
        feeType: unit.feeType || '',
        feeValue: unit.feeValue ?? '',
      });
    } else {
      setEditing(null);
      setFormData({
        unitNumber: '',
        bedrooms: 1,
        bathrooms: 1,
        squareFeet: 0,
        monthlyRent: 0,
        deposit: 0,
        status: 'available',
        amenities: [],
        description: '',
      });
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.unitNumber) {
        toast.error('Unit number is required');
        return;
      }

      if (editing) {
        await axios.put(`/units/${editing}`, formData);
        toast.success('Unit updated successfully');
      } else {
        await axios.post('/units', {
          ...formData,
          property: propertyId
        });
        toast.success('Unit created successfully');
      }
      setOpen(false);
      fetchUnits();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving unit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this unit? This action cannot be undone.')) return;
    try {
      await axios.delete(`/units/${id}`);
      toast.success('Unit deleted successfully');
      fetchUnits();
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting unit');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Units</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Unit
        </Button>
      </Box>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Unit number or tenant"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'status', label: 'Status', value: filterValues.status || '',
            onChange: (v) => setFilter('status', v),
            options: [
              { value: 'available', label: 'Available' },
              { value: 'occupied', label: 'Occupied' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'unavailable', label: 'Unavailable' }
            ] }
        ]}
      />
      <ScrollTable maxHeight="60vh" size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Unit Number</TableCell>
              <TableCell>Bedrooms</TableCell>
              <TableCell>Bathrooms</TableCell>
              <TableCell>Square Feet</TableCell>
              <TableCell>Monthly Rent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUnits.map((unit) => (
              <TableRow key={unit._id}>
                <TableCell className="pin-left">{unit.unitNumber}</TableCell>
                <TableCell>{unit.bedrooms}</TableCell>
                <TableCell>{unit.bathrooms}</TableCell>
                <TableCell>{unit.squareFeet?.toLocaleString()}</TableCell>
                <TableCell>{formatMoney(unit.monthlyRent, { decimals: 0 })}</TableCell>
                <TableCell>
                  <Chip
                    label={unit.status}
                    color={
                      unit.status === 'occupied'
                        ? 'success'
                        : unit.status === 'available'
                        ? 'primary'
                        : unit.status === 'unavailable'
                        ? 'error'
                        : 'warning'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {unit.assignedTenant?.name || '-'}
                </TableCell>
                <TableCell className="pin-right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpen(unit)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {!unit.assignedTenant && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(unit._id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {visibleUnits.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No units found. Add units to this property.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editing ? 'Edit Unit' : 'Add New Unit'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Unit Number/Name"
                required
                value={formData.unitNumber}
                onChange={(e) =>
                  setFormData({ ...formData, unitNumber: e.target.value })
                }
                helperText="e.g., Unit 1, Apartment A, etc."
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Bedrooms"
                type="number"
                required
                value={formData.bedrooms}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Bathrooms"
                type="number"
                required
                value={formData.bathrooms}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                required
                value={formData.squareFeet}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, squareFeet: parseInt(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                required
                value={formData.monthlyRent}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, monthlyRent: parseFloat(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Deposit"
                type="number"
                value={formData.deposit}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  setFormData({ ...formData, deposit: parseFloat(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="occupied">Occupied</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </TextField>
            </Grid>
            <FeeTermsFields
              value={{ feeType: formData.feeType, feeValue: formData.feeValue }}
              onChange={(next) => setFormData({ ...formData, ...next })}
              inheritsFrom="the property's terms"
            />
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnitManagement;

